import { useState, useCallback, useEffect } from 'react';
import { edgeEventCountCardApi } from '../api/edgeEventCountCardApi';

export const useEdgeEventData = (targetEdge, activeFile, api) => {
  const [edgeData, setEdgeData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      // ✅ NEW: Use the unified API hook - automatically applies global filters
      const response = await api.post('/filtered-edge-event-counts', {}, {}, 'Edge Event Counts');
      console.log("🚀 Received Edge Event Counts Response:", response);
      
      // Handle the new filtered response format
      if (response.filtered !== undefined) {
        // New filtered format: single file with counts, ratios, etc.
        const processedData = {};
        const fileKey = activeFile || 'main'; // Use activeFile or fallback to 'main'
        
        let found = false;
        let count = null;
        let ratio = null;

        // Look for the target edge in the filtered response
        for (const objectType in response.counts || {}) {
          const edges = response.counts[objectType];
          
          if (edges[targetEdge] !== undefined) {
            count = edges[targetEdge];
            found = true;
          }
        }

        for (const objectType in response.ratios || {}) {
          if (response.ratios[objectType][targetEdge] !== undefined) {
            ratio = response.ratios[objectType][targetEdge];
          }
        }

        // If not found, try to find similar edges (case-insensitive or partial match)
        if (!found) {
          for (const objectType in response.counts || {}) {
            const edges = response.counts[objectType];
            for (const edgeName in edges) {
              if (edgeName.toLowerCase().includes(targetEdge.toLowerCase()) || 
                  targetEdge.toLowerCase().includes(edgeName.toLowerCase())) {
                count = edges[edgeName];
                found = true;
                console.log(`🔍 Found similar edge "${edgeName}" for target "${targetEdge}": count=${count}`);
                break;
              }
            }
            if (found) break;
          }
        }

        processedData[fileKey] = {
          found,
          count,
          ratio,
          totalAuftrag: response.total_auftrag_count || 0,
          filtered: response.filtered || false,
          filterSummary: response.filter_summary
        };

        setEdgeData(processedData);
      } else {
        // Fallback to old format processing if needed
        const processedData = {};
        Object.entries(response).forEach(([fileType, fileData]) => {
          if (fileData.error) {
            processedData[fileType] = { error: fileData.error, found: false };
            return;
          }

          let found = false;
          let count = null;
          let ratio = null;

          for (const objectType in fileData.counts || {}) {
            if (fileData.counts[objectType][targetEdge] !== undefined) {
              count = fileData.counts[objectType][targetEdge];
              found = true;
            }
          }

          for (const objectType in fileData.ratios || {}) {
            if (fileData.ratios[objectType][targetEdge] !== undefined) {
              ratio = fileData.ratios[objectType][targetEdge];
            }
          }

          processedData[fileType] = {
            found,
            count,
            ratio,
            totalAuftrag: fileData.total_auftrag_count || 0,
          };
        });

        setEdgeData(processedData);
      }
    } catch (err) {
      setError("Failed to load edge count data. " + (err.message || ''));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [targetEdge, activeFile, api]);

  // ✅ NEW: Auto-reload when global filters change
  useEffect(() => {
    if (hasFetched) {
      loadData();
    }
  }, [api.currentFilters, activeFile]);

  const hasData = !isLoading && !error && Object.keys(edgeData).length > 0;

  return { 
    edgeData, 
    isLoading, 
    error, 
    hasFetched, 
    hasData, 
    loadData 
  };
};